import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatNumber } from '@/lib/format';
import { ShareMatrix, type MatrixRow } from './share-matrix';
import { SubjectDot } from './subjects';
import {
  useUsageFlows,
  type FlowCountDto,
  type Subject,
  type SubjectFlowsDto,
  type UsageFilters,
} from './usage-analytics.api';
import { ratio, useDomainLabel, usePageLabel } from './usage-format';
import { Empty, ErrorNotice } from './usage-parts';

/**
 * How a subject moves through an app, rather than how much it uses each page.
 *
 * Everything here is counted in sessions, and a session is a browser tab: the
 * unit the recorder has. Two tabs are two sessions, and a tab left open for a
 * week is one. That is said in the help line, because "sessions" reads as
 * sign-ins to anyone who is not told otherwise.
 */

type FlowTab = 'entries' | 'exits' | 'next' | 'depth';

/** Shortest first; the API sends only the buckets that had sessions. */
const DEPTHS = ['1', '2-5', '6-10', '11+'];

/** Stands in for "no page" in a row id: the session ended. */
const ENDED = '∅';

export interface PageOption {
  domain: string;
  page: string;
}

const optionId = (option: PageOption) => `${option.domain}|${option.page}`;

function usePageRows() {
  const { t } = useTranslation();
  const pageLabel = usePageLabel();
  const domainLabel = useDomainLabel();

  /**
   * One row per page across the subjects, each cell that page's share of the
   * subject's own total. Ranked by the largest share any subject gives it, as
   * the top-pages table is, so a page that matters to one of them is not
   * buried under pages that matter a little to all.
   */
  return (
    subjects: Subject[],
    flows: Map<string, SubjectFlowsDto>,
    pick: (flow: SubjectFlowsDto) => FlowCountDto[],
    total: (flow: SubjectFlowsDto) => number,
    cellTitle: (count: number, total: number) => string,
  ): MatrixRow[] => {
    const rows = new Map<string, MatrixRow & { peak: number }>();

    for (const subject of subjects) {
      const flow = flows.get(subject.key);
      if (!flow) continue;
      const of = total(flow);

      for (const entry of pick(flow)) {
        const id =
          entry.page === null ? ENDED : `${entry.domain}|${entry.page}`;
        const row = rows.get(id) ?? {
          id,
          label:
            entry.page === null ? (
              <span className="text-muted-foreground italic">
                {t('usage.sessionEnded')}
              </span>
            ) : (
              <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span>{pageLabel(entry.domain ?? '', entry.page)}</span>
                <Badge variant="outline" className="text-[10px] font-normal">
                  {domainLabel(entry.domain ?? '')}
                </Badge>
              </span>
            ),
          cells: new Map(),
          peak: 0,
        };
        const share = ratio(entry.count, of);
        row.cells.set(subject.key, {
          share,
          title: cellTitle(entry.count, of),
        });
        row.peak = Math.max(row.peak, share);
        rows.set(id, row);
      }
    }

    return [...rows.values()].sort((a, b) => b.peak - a.peak);
  };
}

export function UsageFlows({
  filters,
  subjects,
  pageOptions,
}: {
  filters: UsageFilters;
  subjects: Subject[];
  /** Pages to offer in the "what comes after" picker, busiest first. */
  pageOptions: PageOption[];
}) {
  const { t } = useTranslation();
  const pageLabel = usePageLabel();
  const domainLabel = useDomainLabel();
  const pageRows = usePageRows();

  const [tab, setTab] = useState<FlowTab>('entries');
  const [picked, setPicked] = useState<string | null>(null);
  // Falls back to the busiest page rather than holding a choice the current
  // filters no longer offer.
  const from =
    pageOptions.find((option) => optionId(option) === picked) ??
    pageOptions[0] ??
    null;

  // The page asked about only changes the answer on its own tab, so it is
  // only sent there: the other three tabs then share one cached response
  // however often the picker is moved.
  const flows = useUsageFlows(filters, subjects, tab === 'next' ? from : null);

  const bySubject = useMemo(
    () => new Map((flows.data ?? []).map((entry) => [entry.key, entry])),
    [flows.data],
  );

  if (flows.isError) {
    return (
      <ErrorNotice
        message={flows.error?.message || t('usage.loadFailed')}
        onRetry={() => void flows.refetch()}
      />
    );
  }

  const sessionTitle = (count: number, total: number) =>
    t('usage.flowCell', {
      part: formatNumber(count),
      total: formatNumber(total),
    });

  const rows: MatrixRow[] =
    tab === 'entries'
      ? pageRows(
          subjects,
          bySubject,
          (f) => f.entries,
          (f) => f.sessions,
          sessionTitle,
        )
      : tab === 'exits'
        ? pageRows(
            subjects,
            bySubject,
            (f) => f.exits,
            (f) => f.sessions,
            sessionTitle,
          )
        : tab === 'next'
          ? pageRows(
              subjects,
              bySubject,
              (f) => f.next ?? [],
              (f) => f.fromVisits ?? 0,
              (count, total) =>
                t('usage.nextCell', {
                  part: formatNumber(count),
                  total: formatNumber(total),
                }),
            )
          : DEPTHS.flatMap((bucket) => {
              const cells: MatrixRow['cells'] = new Map();
              for (const subject of subjects) {
                const flow = bySubject.get(subject.key);
                const sessions =
                  flow?.depth.find((entry) => entry.pages === bucket)
                    ?.sessions ?? 0;
                if (!flow || flow.sessions === 0) continue;
                // Zero is shown here where the page tables show a dash: the
                // four buckets are the whole of the scale, and a missing one
                // is a real nought, not a page that fell outside the list.
                cells.set(subject.key, {
                  share: ratio(sessions, flow.sessions),
                  title: sessionTitle(sessions, flow.sessions),
                });
              }
              return cells.size > 0
                ? [
                    {
                      id: bucket,
                      label: t(
                        `usage.depth.${bucket === '1' ? 'one' : bucket === '11+' ? 'many' : 'range'}`,
                        {
                          range: bucket.replace('-', '–'),
                        },
                      ),
                      cells,
                    },
                  ]
                : [];
            });

  const anySessions = subjects.some(
    (subject) => (bySubject.get(subject.key)?.sessions ?? 0) > 0,
  );

  return (
    <Tabs value={tab} onValueChange={(value) => setTab(value as FlowTab)}>
      <TabsList>
        <TabsTrigger value="entries">{t('usage.flowEntries')}</TabsTrigger>
        <TabsTrigger value="exits">{t('usage.flowExits')}</TabsTrigger>
        <TabsTrigger value="next">{t('usage.flowNext')}</TabsTrigger>
        <TabsTrigger value="depth">{t('usage.flowDepth')}</TabsTrigger>
      </TabsList>

      {/* One body for all four: the tabs change what the rows are, not the
          shape of what is drawn, and four copies of it would each need the
          same loading and empty states kept in step. */}
      <TabsContent value={tab} className="space-y-3">
        {tab === 'next' && pageOptions.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-muted-foreground text-sm">
              {t('usage.flowAfter')}
            </span>
            <Select
              value={from ? optionId(from) : ''}
              onValueChange={setPicked}
            >
              <SelectTrigger
                className="w-full sm:w-80"
                aria-label={t('usage.flowAfter')}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageOptions.map((option) => (
                  <SelectItem key={optionId(option)} value={optionId(option)}>
                    {pageLabel(option.domain, option.page)}
                    <span className="text-muted-foreground ml-1.5 text-xs">
                      {domainLabel(option.domain)}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {flows.isLoading ? (
          <Skeleton className="h-56 rounded-xl" />
        ) : !anySessions ? (
          <Empty>{t('usage.noSessions')}</Empty>
        ) : rows.length === 0 ? (
          <Empty>
            {t(tab === 'next' ? 'usage.noNext' : 'usage.noSessions')}
          </Empty>
        ) : (
          <ShareMatrix
            heading={t(
              tab === 'depth' ? 'usage.flowDepthHeading' : 'usage.page',
            )}
            subjects={subjects}
            rows={rows}
            busy={flows.isFetching}
          />
        )}

        {/* What every share above is out of. Without it "40% of sessions
            start here" could be four sessions or four thousand. */}
        {anySessions && (
          <ul className="text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 text-xs">
            {subjects.map((subject) => {
              const flow = bySubject.get(subject.key);
              if (!flow) return null;
              return (
                <li key={subject.key} className="flex items-center gap-1.5">
                  <SubjectDot subject={subject} className="size-2" />
                  {tab === 'next'
                    ? t('usage.fromVisitCount', {
                        count: flow.fromVisits ?? 0,
                        formatted: formatNumber(flow.fromVisits ?? 0),
                      })
                    : t('usage.sessionCount', {
                        count: flow.sessions,
                        formatted: formatNumber(flow.sessions),
                      })}
                </li>
              );
            })}
          </ul>
        )}
      </TabsContent>
    </Tabs>
  );
}
